import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Ship, Warehouse, Anchor, MapPin } from "lucide-react";

// Presentation-only mapping. Canonical backend statuses are untouched:
// IN_MOROCCO → Fez · DEPARTED → In Transit · ARRIVING → Arriving · RECEIVED → Florida
const DISPLAY_STATUS = { DEPARTED: "In Transit", ARRIVING: "Arriving" };

function progress(c) {
  // Approximate Atlantic crossing from existing dates only — never live tracking.
  const dep = Date.parse(c.departure_date);
  const eta = Date.parse(c.eta_warehouse || c.eta_carrier);
  if (Number.isFinite(dep) && Number.isFinite(eta) && eta > dep) {
    return Math.min(0.92, Math.max(0.08, (Date.now() - dep) / (eta - dep)));
  }
  return c.status === "ARRIVING" ? 0.85 : 0.5;
}

const labelCls = "text-xs font-semibold uppercase tracking-[0.2em] text-terracotta";
const subCls = "text-xs text-muted-foreground";

// Fixed horseshoe route — FEZ (bottom-left) → CASABLANCA (top-left) → ocean arc
// → MIAMI (top-right) → WEST PALM BEACH (bottom-right). Geometry is constant
// regardless of container status; WPB is always the final endpoint after Miami.
const RW = 400;
const RH = 200;
const PT = {
  fez: { x: 38, y: 152 },
  casablanca: { x: 64, y: 56 },
  miami: { x: 336, y: 56 },
  wpb: { x: 362, y: 152 },
};
const SEG_FEZ_CASA = [PT.fez, { x: 30, y: 110 }, { x: 42, y: 78 }, PT.casablanca];
const SEG_OCEAN = [PT.casablanca, { x: 130, y: 16 }, { x: 270, y: 16 }, PT.miami];
const SEG_MIAMI_WPB = [PT.miami, { x: 358, y: 78 }, { x: 370, y: 110 }, PT.wpb];
const segPath = (p) =>
  `M ${p[0].x} ${p[0].y} C ${p[1].x} ${p[1].y}, ${p[2].x} ${p[2].y}, ${p[3].x} ${p[3].y}`;

function cubicAt(p, t) {
  const u = 1 - t;
  return {
    x: u * u * u * p[0].x + 3 * u * u * t * p[1].x + 3 * u * t * t * p[2].x + t * t * t * p[3].x,
    y: u * u * u * p[0].y + 3 * u * u * t * p[1].y + 3 * u * t * t * p[2].y + t * t * t * p[3].y,
  };
}
function cubicAngle(p, t) {
  const u = 1 - t;
  const dx = 3 * u * u * (p[1].x - p[0].x) + 6 * u * t * (p[2].x - p[1].x) + 3 * t * t * (p[3].x - p[2].x);
  const dy = 3 * u * u * (p[1].y - p[0].y) + 6 * u * t * (p[2].y - p[1].y) + 3 * t * t * (p[3].y - p[2].y);
  return (Math.atan2(dy, dx) * 180) / Math.PI;
}
const pctX = (x) => `${(x / RW) * 100}%`;
const pctY = (y) => `${(y / RH) * 100}%`;

function RouteArrow({ seg, t, color }) {
  const p = cubicAt(seg, t);
  const a = cubicAngle(seg, t);
  return (
    <g transform={`translate(${p.x} ${p.y}) rotate(${a})`} aria-hidden="true">
      <path d="M -4.5 -4 L 5 0 L -4.5 4 z" fill={color} />
    </g>
  );
}

function Node({ testid, icon: Icon, title, sub, onClick, x, y }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={!onClick}
      style={{ left: pctX(x), top: pctY(y) }}
      className={`absolute z-10 flex w-12 -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0.5 sm:w-20 ${onClick ? "cursor-pointer" : "cursor-default"}`}>
      <span className={`flex h-8 w-8 items-center justify-center rounded-full border bg-white sm:h-11 sm:w-11 ${onClick ? "border-terracotta text-terracotta transition-colors hover:bg-terracotta hover:text-white" : "border-limestone text-warmgrey"}`}>
        <Icon className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
      </span>
      <span className="text-[9px] font-semibold uppercase leading-tight tracking-wider text-charcoal sm:text-xs">{title}</span>
      <span className="text-[9px] text-warmgrey sm:text-[10px]">{sub}</span>
    </button>
  );
}

export function InventoryJourney({ journey }) {
  const [drill, setDrill] = useState(null);
  const navigate = useNavigate();
  if (!journey) return null;
  const { morocco, containers, florida } = journey;
  // Multi-container stages (§H): every real container sits at its operational stage.
  const stageOf = (c) => c.stage || (c.status === "IN_MOROCCO" ? "preparing" : c.status === "ARRIVING" ? "arriving" : c.status === "RECEIVED" ? "received" : "in_transit");
  const preparing = containers.filter((c) => stageOf(c) === "preparing");
  const transit = containers.filter((c) => stageOf(c) === "in_transit");
  const arriving = containers.filter((c) => stageOf(c) === "arriving");
  const received = containers.filter((c) => stageOf(c) === "received");
  const inTransit = transit.length > 0;
  const trackColor = "#E3DCD2";
  const oceanColor = inTransit ? "#BC6955" : "#E3DCD2";

  const close = () => setDrill(null);

  const drillLink = (to, label) => (
    <button type="button" data-testid="journey-drill-link" onClick={() => { close(); navigate(to); }}
      className="mt-4 text-xs font-semibold uppercase tracking-wider text-terracotta hover:text-terracotta-hover">
      {label} →
    </button>
  );

  const Breakdown = ({ items, showAvailability }) => (
    <div className="mt-3 max-h-56 space-y-2 overflow-y-auto" data-testid="journey-drill-body">
      {items.length === 0 && <p className={subCls}>No inventory recorded.</p>}
      {items.map((i) => (
        <div key={i.variant_id} className="flex items-baseline justify-between gap-3 border-b border-limestone pb-2 text-sm">
          <span className="text-charcoal">{i.label}</span>
          <span className="shrink-0 text-xs text-warmgrey">
            {showAvailability
              ? `${i.boxes - i.reserved - i.committed} avail · ${i.reserved} res · ${i.committed} com`
              : `${i.boxes} boxes`} · {i.sqft.toLocaleString()} sq ft
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="mt-8" data-testid="inventory-journey">
      <p className={labelCls}>Morocco → Florida</p>
      <div className="relative mt-4 rounded-lg border border-border bg-white px-2 py-4 sm:px-6 sm:py-6">
        <div className="relative">
          <svg viewBox={`0 0 ${RW} ${RH}`} className="block h-auto w-full" aria-hidden="true">
            {/* Fez → Casablanca */}
            <path d={segPath(SEG_FEZ_CASA)} stroke={trackColor} strokeWidth="2" fill="none" />
            {/* Casablanca → Miami (ocean / in transit — de-emphasized when empty) */}
            <path d={segPath(SEG_OCEAN)} data-testid="journey-atlantic" stroke={oceanColor} strokeWidth="2"
              strokeDasharray="6 5" fill="none" opacity={inTransit ? 0.9 : 0.45} />
            {/* Miami → West Palm Beach */}
            <path d={segPath(SEG_MIAMI_WPB)} stroke={trackColor} strokeWidth="2" fill="none" />
            {/* directional arrows — the sequence is always FEZ→CASABLANCA→OCEAN→MIAMI→WPB */}
            <RouteArrow seg={SEG_FEZ_CASA} t={0.55} color={trackColor} />
            <RouteArrow seg={SEG_OCEAN} t={0.24} color={oceanColor} />
            <RouteArrow seg={SEG_OCEAN} t={0.76} color={oceanColor} />
            <RouteArrow seg={SEG_MIAMI_WPB} t={0.5} color={trackColor} />
          </svg>
          <div className="absolute inset-0">
            <p className="absolute -translate-x-1/2 text-center text-[9px] font-semibold uppercase tracking-[0.3em] text-charcoal/30 sm:text-[10px]"
              style={{ left: pctX(200), top: pctY(6) }} data-testid="journey-ocean-label">
              Ocean — In transit
            </p>
            {!inTransit && (
              <p className="absolute -translate-x-1/2 whitespace-nowrap text-center text-[10px] uppercase tracking-wider text-warmgrey"
                style={{ left: pctX(200), top: pctY(104) }} data-testid="journey-no-transit">
                No containers currently in transit
              </p>
            )}
            {transit.map((c, idx) => {
              const p = cubicAt(SEG_OCEAN, 0.08 + progress(c) * 0.84);
              return (
                <button key={c.id} type="button" data-testid={`journey-ship-${idx}`} title={`${c.display_name || c.container_number} — ${DISPLAY_STATUS[c.status] || "In transit"}`}
                  onClick={() => setDrill({ type: "ship", c })}
                  className="absolute z-10 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-terracotta text-white shadow-sm transition-transform hover:scale-110 sm:h-9 sm:w-9"
                  style={{ left: pctX(p.x), top: pctY(p.y), marginTop: idx % 2 === 0 ? -8 : 8 }}>
                  <Ship className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>
              );
            })}
            <Node testid="journey-node-fez" icon={Warehouse} title="Fez" x={PT.fez.x} y={PT.fez.y}
              sub={`${morocco.total_boxes} boxes`} onClick={() => setDrill({ type: "fez" })} />
            <Node testid="journey-node-casablanca" icon={Anchor} title="Casablanca" x={PT.casablanca.x} y={PT.casablanca.y}
              sub={preparing.length ? `${preparing.length} preparing` : "departure"}
              onClick={preparing.length ? () => setDrill({ type: "stage", stage: "preparing", list: preparing }) : undefined} />
            <Node testid="journey-node-miami" icon={MapPin} title="Miami" x={PT.miami.x} y={PT.miami.y}
              sub={arriving.length ? `${arriving.length} arriving` : "arrival"} onClick={() => setDrill({ type: "miami" })} />
            <Node testid="journey-node-wpb" icon={Warehouse} title="West Palm Beach" x={PT.wpb.x} y={PT.wpb.y}
              sub={`${florida.available} avail`} onClick={() => setDrill({ type: "wpb" })} />
          </div>
        </div>
        <p className="mt-2 text-center text-[10px] uppercase tracking-wider text-warmgrey">
          Positions approximated from departure/ETA dates — not live tracking
        </p>
      </div>

      <Dialog open={!!drill} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-md sm:!max-h-[85dvh] sm:!overflow-y-auto" data-testid="journey-drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-medium text-charcoal" data-testid="journey-drill-title">
              {drill?.type === "fez" && "Morocco inventory — Fez"}
              {drill?.type === "ship" && (drill.c.display_name || drill.c.container_number)}
              {drill?.type === "stage" && "Preparing — Fez / Casablanca"}
              {drill?.type === "miami" && "Arriving — Miami"}
              {drill?.type === "wpb" && "Florida inventory — West Palm Beach"}
            </DialogTitle>
          </DialogHeader>

          {drill?.type === "fez" && (
            <>
              <p className={subCls}>{morocco.total_boxes} boxes · {morocco.total_sqft.toLocaleString()} sq ft in Morocco</p>
              <Breakdown items={morocco.items} />
              {drillLink("/admin/inventory", "Open inventory")}
            </>
          )}

          {drill?.type === "ship" && (
            <div className="space-y-2 text-sm" data-testid="journey-drill-body">
              <p className={subCls}>Internal ref: <span className="font-mono text-xs text-charcoal">{drill.c.container_number}</span></p>
              <p className={subCls}>Status: <span className="font-medium text-charcoal">{DISPLAY_STATUS[drill.c.status] || (drill.c.status === "RECEIVED" ? "Received" : drill.c.status === "IN_MOROCCO" ? "Preparing" : "In transit")}</span></p>
              <p className={subCls}>Departure: <span className="text-charcoal">{drill.c.departure_date || "—"}</span></p>
              <p className={subCls}>ETA warehouse: <span className="text-charcoal">{drill.c.eta_warehouse || drill.c.eta_carrier || "—"}</span></p>
              <p className={subCls}>{drill.c.total_boxes} boxes · {drill.c.total_sqft.toLocaleString()} sq ft · {drill.c.spoken_boxes} spoken for</p>
              <div className="mt-2 space-y-1 border-t border-limestone pt-3">
                {drill.c.contents.map((l, i) => (
                  <p key={i} className="flex justify-between text-xs"><span className="text-charcoal">{l.label}</span><span className="text-warmgrey">{l.boxes} boxes</span></p>
                ))}
              </div>
              {drillLink(`/admin/containers/${drill.c.id}`, "Open container")}
            </div>
          )}

          {drill?.type === "stage" && (
            <>
              <div className="mt-2 space-y-2" data-testid="journey-drill-body">
                {(drill.list || []).map((c) => (
                  <button key={c.id} type="button" onClick={() => setDrill({ type: "ship", c })} data-testid={`journey-stage-item-${c.id}`}
                    className="flex w-full items-center justify-between rounded-md border border-limestone p-3 text-left transition-colors hover:bg-sand">
                    <span className="text-sm font-medium text-charcoal">{c.display_name || c.container_number}</span>
                    <span className="text-xs text-warmgrey">{c.total_boxes} boxes{c.eta_warehouse || c.eta_carrier ? ` · ETA ${c.eta_warehouse || c.eta_carrier}` : ""}</span>
                  </button>
                ))}
              </div>
              {drillLink("/admin/containers", "Open containers")}
            </>
          )}

          {drill?.type === "miami" && (
            <>
              <div className="mt-2 space-y-2" data-testid="journey-drill-body">
                {arriving.length === 0 && <p className={subCls}>No containers currently arriving.</p>}
                {arriving.map((c) => (
                  <button key={c.id} type="button" onClick={() => setDrill({ type: "ship", c })}
                    className="flex w-full items-center justify-between rounded-md border border-limestone p-3 text-left transition-colors hover:bg-sand">
                    <span className="text-sm font-medium text-charcoal">{c.display_name || c.container_number}</span>
                    <span className="text-xs text-warmgrey">ETA {c.eta_warehouse || c.eta_carrier || "—"}</span>
                  </button>
                ))}
              </div>
              {drillLink("/admin/containers", "Open containers")}
            </>
          )}

          {drill?.type === "wpb" && (
            <>
              {received.length > 0 && (
                <div className="mb-3 space-y-2" data-testid="journey-received-list">
                  <p className={subCls}>{received.length} container(s) received:</p>
                  {received.map((c) => (
                    <button key={c.id} type="button" onClick={() => setDrill({ type: "ship", c })} data-testid={`journey-received-${c.id}`}
                      className="flex w-full items-center justify-between rounded-md border border-limestone p-3 text-left transition-colors hover:bg-sand">
                      <span className="text-sm font-medium text-charcoal">{c.display_name || c.container_number}</span>
                      <span className="text-xs text-warmgrey">{c.total_boxes} boxes · received</span>
                    </button>
                  ))}
                </div>
              )}
              <p className={subCls} data-testid="journey-wpb-totals">
                {florida.physical} physical · {florida.available} available · {florida.reserved} reserved · {florida.committed} committed · {florida.total_sqft.toLocaleString()} sq ft
              </p>
              <Breakdown items={florida.items} showAvailability />
              {drillLink("/admin/inventory", "Open inventory")}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
